import { Camera, FileText, AlertTriangle, Plus, Trash2, ChevronRight, ChevronLeft, Loader2, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { ImportSessionViewProps } from "@/ui/view-props";

function StepDots({ step }: { step: 1 | 2 | 3 }) {
  const labels = ["Input", "Review", "Confirm"];
  return (
    <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-full border border-white/5">
      {([1, 2, 3] as const).map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={cn(
              "h-2 rounded-full transition-all duration-300",
              s === step ? "w-6 bg-neon-cyan shadow-[0_0_8px_hsl(var(--neon-cyan))]" : 
              s < step ? "w-2 bg-neon-emerald" : "w-2 bg-neon-border"
            )}
          />
          <span
            className={cn(
              "text-[10px] font-black uppercase tracking-widest",
              s === step ? "text-white" : "text-neon-muted"
            )}
          >
            {labels[i]}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ImportSessionNeonView(p: ImportSessionViewProps) {
  const dupes = p.getDuplicateNames();
  const hasDupes = p.hasDuplicateNames();

  return (
    <div className="animate-fade-in flex flex-col gap-6">

      {/* ── Header ──────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-1">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight uppercase leading-none">Import Session</h1>
          <div className="text-xs text-neon-muted font-bold tracking-widest mt-1 flex items-center gap-1.5 uppercase">
            <span className="w-1 h-1 rounded-full bg-neon-cyan" />
            Scan or paste your score sheet
          </div>
        </div>
        <StepDots step={p.step} />
      </div>

      {/* ── Step 1: Input ──────────────────────────────────── */}
      {p.step === 1 && (
        <div className="animate-slide-up flex flex-col gap-6">
          {p.settings.enable_vision && (
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                className="glass-card p-6 flex flex-col items-center gap-3 group"
                onClick={p.onTakePhotoClick}
                disabled={p.loading}
              >
                <div className="w-14 h-14 rounded-2xl grad-cyan-emerald flex items-center justify-center shadow-lg shadow-neon-cyan/20 group-hover:scale-110 transition-transform">
                  <Camera className="w-7 h-7 text-white" />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-sm font-black text-white uppercase tracking-wider">Take Photo</span>
                  <span className="text-[10px] font-bold text-neon-muted uppercase">Use camera</span>
                </div>
              </button>
              <button
                type="button"
                className="glass-card p-6 flex flex-col items-center gap-3 group"
                onClick={p.onLibraryClick}
                disabled={p.loading}
              >
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-neon-cyan" />
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-sm font-black text-white uppercase tracking-wider">Library</span>
                  <span className="text-[10px] font-bold text-neon-muted uppercase">Pick image</span>
                </div>
              </button>
            </div>
          )}

          {/* image preview */}
          {p.previewUrl && (
            <div className="glass-card p-2">
              <img src={p.previewUrl} alt="Preview" className="w-full max-h-[300px] object-contain rounded-xl" />
            </div>
          )}

          {p.loading && (
            <div className="glass-card p-8 flex flex-col items-center gap-4 text-center border-neon-cyan/30 bg-neon-cyan/5">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-neon-cyan animate-spin" />
                <Sparkles className="w-5 h-5 text-neon-cyan absolute -top-1 -right-1 animate-pulse" />
              </div>
              <div>
                <div className="text-base font-black text-white uppercase tracking-wider">Analyzing Intelligence</div>
                <div className="text-xs text-neon-muted font-bold mt-1">Extracting scores via Pixtral Vision... (10-20s)</div>
              </div>
            </div>
          )}

          {/* text input */}
          <div className="glass-card p-6 flex flex-col gap-4">
            <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest flex items-center gap-2">
              <FileText className="w-3 h-3" /> Paste Score Text
            </div>
            <textarea
              className="neon-input bg-white/5 min-h-[140px] text-sm py-4 focus:border-neon-cyan"
              placeholder="Paste score sheet text, JSON, or WhatsApp results here..."
              value={p.rawText}
              onChange={(e) => p.setRawText(e.target.value)}
            />

            <div className="flex flex-col gap-3">
              <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest">Buy-in unit</div>
              <div className="flex gap-2 flex-wrap items-center">
                {[10, 20, 50, 100].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={cn(
                      "quick-pill px-4 py-2 text-xs font-bold",
                      p.buyInUnit === preset ? "bg-neon-cyan/20 border-neon-cyan text-neon-cyan shadow-[0_0_10px_hsl(var(--neon-cyan)/0.1)]" : ""
                    )}
                    onClick={() => p.setBuyInUnit(preset)}
                  >
                    {preset}
                  </button>
                ))}
                <input
                  type="number"
                  inputMode="numeric"
                  className="neon-input w-20 bg-white/5 text-center text-sm font-bold"
                  value={p.buyInUnit}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (v > 0) p.setBuyInUnit(v);
                  }}
                />
              </div>
            </div>

            <button
              type="button"
              className="neon-btn-primary w-full py-4 flex items-center justify-center gap-2 rounded-xl mt-2"
              onClick={p.onTextProcess}
              disabled={p.loading || !p.rawText.trim()}
            >
              <span className="font-black uppercase tracking-widest">Process Data</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <button
            type="button"
            className="text-xs font-black text-neon-muted uppercase tracking-widest hover:text-white transition-colors"
            onClick={p.onManualEntry}
          >
            Or skip to manual entry
          </button>

          {/* error */}
          {p.error && (
            <div className="glass-card p-6 border-neon-rose/40 bg-neon-rose/5 animate-slide-up">
              <div className="flex gap-4">
                <AlertTriangle className="w-6 h-6 text-neon-rose shrink-0" />
                <div>
                  <div className="text-sm font-black text-neon-rose uppercase tracking-wider">Extraction Failed</div>
                  <div className="text-xs text-neon-muted font-medium mt-1 leading-relaxed">{p.error}</div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" className="neon-btn-secondary flex-1 text-[10px] font-black uppercase" onClick={p.onTryAgain}>Try again</button>
                <button type="button" className="neon-btn-secondary flex-1 text-[10px] font-black uppercase" onClick={p.onManualEntry}>Enter manually</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: Review / edit ──────────────────────────── */}
      {p.step === 2 && p.data && (
        <div className="animate-slide-up flex flex-col gap-6">
          {/* date */}
          <div className="glass-card p-5 flex items-center justify-between">
            <span className="text-[10px] font-black text-neon-muted uppercase tracking-widest">Session Date</span>
            <input
              type="date"
              className="neon-input w-auto bg-white/5 border-none text-sm font-bold"
              value={p.data.sessionDate || ""}
              onChange={(e) => p.setData((prev) => prev ? { ...prev, sessionDate: e.target.value } : prev)}
            />
          </div>

          <datalist id="neon-existing-players">
            {p.allPlayers?.map((player) => (
              <option key={player.id} value={player.name} />
            ))}
          </datalist>

          {/* Stats Summary */}
          <div className="glass-card grid grid-cols-2 md:grid-cols-4 overflow-hidden divide-x divide-y divide-white/5 md:divide-y-0">
            <div className="p-4 flex flex-col items-center gap-1">
              <div className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Total Pot</div>
              <div className="text-base font-black text-neon-cyan tabular-nums">{p.data.totalPot.toFixed(0)}</div>
            </div>
            <div className="p-4 flex flex-col items-center gap-1">
              <div className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Total Won</div>
              <div className="text-base font-black text-neon-green tabular-nums">{p.data.totalPositive.toFixed(0)}</div>
            </div>
            <div className="p-4 flex flex-col items-center gap-1">
              <div className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Total Lost</div>
              <div className="text-base font-black text-neon-crimson tabular-nums">{p.data.totalNegative.toFixed(0)}</div>
            </div>
            <div className="p-4 flex flex-col items-center gap-1">
              <div className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Variance</div>
              <div className={cn(
                "text-base font-black tabular-nums",
                Math.abs(p.data.variance) < 0.01 ? "text-neon-emerald" : "text-neon-amber"
              )}>
                {Math.abs(p.data.variance) < 0.01 ? "0" : p.data.variance.toFixed(0)}
              </div>
            </div>
          </div>

          {/* player rows */}
          <div className="flex flex-col gap-3">
            {p.data.players.map((pl, idx) => {
              const isDupe = dupes.has(pl.name.trim().toLowerCase()) && pl.name.trim();
              const hasVariance = Math.abs(pl.variance) > 0.01;
              return (
                <div
                  key={idx}
                  className={cn(
                    "glass-card p-5 flex flex-col gap-4 transition-all",
                    isDupe ? "border-neon-rose/50" : hasVariance ? "border-neon-amber/50" : ""
                  )}
                >
                  <div className="flex gap-3 items-center">
                    <input
                      className="neon-input flex-1 bg-white/5 font-bold"
                      placeholder="Player name"
                      value={pl.name}
                      list="neon-existing-players"
                      onChange={(e) => p.updatePlayer(idx, "name", e.target.value)}
                    />
                    <button
                      type="button"
                      className="p-2.5 rounded-xl bg-white/5 text-neon-muted hover:text-neon-crimson transition-colors"
                      onClick={() => p.removePlayer(idx)}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {(["buyinCount", "cashoutAmount", "net"] as const).map((field) => (
                      <div key={field}>
                        <div className="text-[9px] font-black text-neon-muted uppercase tracking-tighter mb-1 ml-1">
                          {field === "buyinCount" ? "Buy-ins" : field === "cashoutAmount" ? "Cash-out" : "Net"}
                        </div>
                        <input
                          type="number"
                          className={cn(
                            "neon-input bg-white/5 py-1.5 px-2 text-sm text-center font-bold",
                            field === "net" && (pl.net > 0 ? "text-neon-green" : pl.net < 0 ? "text-neon-crimson" : "")
                          )}
                          value={(pl as any)[field]}
                          onChange={(e) => p.updatePlayer(idx, field, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="neon-btn-secondary w-full py-3 flex items-center justify-center gap-2 border-dashed border-white/10"
            onClick={p.addPlayer}
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-black uppercase tracking-widest">Add Player</span>
          </button>

          <div className="flex gap-3 sticky-mobile-footer bg-neon-bg/80 backdrop-blur-xl border-t border-white/5 p-4">
            <button
              type="button"
              className="neon-btn-secondary px-6 py-4 rounded-2xl"
              onClick={() => p.setStep(1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="neon-btn-primary flex-1 py-4 flex items-center justify-center gap-2 rounded-2xl shadow-xl shadow-neon-cyan/20"
              onClick={() => p.setStep(3)}
              disabled={hasDupes}
            >
              <span className="font-black uppercase tracking-widest">Review Final</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Confirm ─────────────────────────────────── */}
      {p.step === 3 && p.data && (
        <div className="animate-slide-up flex flex-col gap-6">
          <div className="glass-card p-6 flex flex-col gap-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-lg font-black text-white uppercase">{format(new Date(p.data.sessionDate || Date.now()), "MMMM d, yyyy")}</div>
                <div className="text-xs text-neon-muted font-bold uppercase tracking-widest">{p.data.players.length} Players joined</div>
              </div>
              <div className="px-3 py-1 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan text-[10px] font-black uppercase tracking-widest">
                FINAL REVIEW
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <div className="text-[9px] font-black text-neon-muted uppercase">Won</div>
                <div className="text-sm font-black text-neon-green tabular-nums">{p.data.totalPositive.toFixed(0)}</div>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <div className="text-[9px] font-black text-neon-muted uppercase">Lost</div>
                <div className="text-sm font-black text-neon-crimson tabular-nums">{p.data.totalNegative.toFixed(0)}</div>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <div className="text-[9px] font-black text-neon-muted uppercase">Pot</div>
                <div className="text-sm font-black text-neon-cyan tabular-nums">{p.data.totalPot.toFixed(0)}</div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              {p.data.players.filter((pl) => pl.name.trim()).map((pl, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div>
                    <div className="text-sm font-black text-white">{pl.name}</div>
                    <div className="text-[9px] font-bold text-neon-muted uppercase tracking-tighter">{pl.buyinCount}× BUY-IN</div>
                  </div>
                  <div className={cn(
                    "text-sm font-black tabular-nums tracking-tight",
                    pl.net > 0 ? "text-neon-green" : pl.net < 0 ? "text-neon-crimson" : "text-neon-muted"
                  )}>
                    {pl.net >= 0 ? "+" : ""}{pl.net.toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 sticky-mobile-footer bg-neon-bg/80 backdrop-blur-xl border-t border-white/5 p-4">
            <button
              type="button"
              className="neon-btn-secondary px-6 py-4 rounded-2xl"
              onClick={() => p.setStep(2)}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="neon-btn-primary flex-1 py-4 flex items-center justify-center gap-2 rounded-2xl shadow-xl shadow-neon-emerald/40 !bg-neon-emerald"
              onClick={p.saveSession}
              disabled={p.saving}
            >
              {p.saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              <span className="font-black uppercase tracking-widest">
                {p.saving ? "Saving..." : "Create Session"}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
